/* eslint-disable no-mixed-operators */
import * as React from 'react';
import { Icon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import Color from './Color';
import { IPersonaProps, Persona, PersonaSize } from '@fluentui/react/lib/Persona';
import _ from 'lodash';
import RbLabel, { LabelSize } from '../../bosch-react/components/label/RbLabel';
import RbTag from '../../bosch-react/components/tag/RbTag';
import { Get, IGroup, IUser, MgtTemplateProps, People, Person } from '@microsoft/mgt-react';
import './Template.scss'
import { mergeStyles, mergeStyleSets, Shimmer, Spinner, SpinnerLabelPosition } from '@fluentui/react';
import { addAliasDomain } from '../../utils/converter';
import { PrincipalType } from '../components/PeoplePicker/types';
import CustomizedPeoplePicker from '../components/PeoplePicker/CustomizedPeoplePicker';

export interface IPersonDetail {
    displayName: string,
    mail: string,
    department: string,
    userPrincipalName: string,
    userRole: string[],
}

class Template {

    static renderCommonTemplate(label: any, field: any, isOptional: boolean = false, labelWidth: number = 2, fieldWidth: number = 10, parentWidth: number = 12) {
        return (
            <div className={"ms-Grid-col ms-sm" + parentWidth}>
                <div className="ms-Grid">
                    <div className="ms-Grid-row common-padding-row">
                        <div className={"ms-Grid-col ms-sm" + labelWidth} style={{ verticalAlign: "top" }}>
                            <RbLabel hasPadding={true}>{label}{isOptional === true ? " (Optional)" : ""}</RbLabel>
                        </div>
                        <div className={"ms-Grid-col ms-sm" + fieldWidth}>
                            {field}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    static renderUploadTemplate(label: string, isRequired: boolean, labelMoreInformation: string = "", field: any, tooltipMessage: any = "", moreInformation: any = "") {
        return (
            <div>
                <div className="ms-Grid-row common-padding-row">
                    <div className="ms-Grid-col ms-sm3"><RbLabel hasPadding={true}>{label}{(isRequired === false) ? <span> (Optional)</span> : ""}{labelMoreInformation}</RbLabel></div>
                    <div className="ms-Grid-col ms-sm5">
                        {field}
                    </div>
                    {(tooltipMessage !== "") ? <div className="ms-Grid-col ms-sm4 upload-tooltip">
                        <div className="ms-Grid-col ms-sm1 upload-icon-tooltip"><Icon iconName="AlertSolid" /></div>
                        <div className="ms-Grid-col ms-sm3 upload-message-tooltip"><RbLabel>{tooltipMessage}</RbLabel></div>
                    </div> : ""}
                </div>
                {(moreInformation !== "") ?
                    <div className="ms-Grid-row common-padding-row">
                        <div className="ms-Grid-col ms-sm3" />
                        <div className="ms-Grid-col ms-sm5"><RbLabel style={{ color: Color.GREY }}>{moreInformation}</RbLabel></div>
                    </div> : ""}
            </div>
        );
    }

    static renderAccessMediatorRowTemplate(mode: string, label: string, viewField: any, editField: any, loadingElement: any = null, errorTxt: string = "", isOptional: boolean = false, customizedOptionalText: string = "",
        labelWidth: number = 4, fieldWidth: number = 8) {
        let field: any = (mode === "Edit") ? <React.Fragment>
            {editField}
            {!!errorTxt ? <RbLabel className="error-txt" size={LabelSize.Small} isInline={false} hasPadding={true}> {errorTxt}</RbLabel> : null}
        </React.Fragment> : !!loadingElement ? loadingElement : viewField;
        return (
            <div className="ms-Grid">
                <div className="ms-Grid-row common-padding-row">
                    {/* Label */}
                    <div className={"ms-Grid-col ms-sm" + labelWidth}>
                        <RbLabel hasPadding={true}>{label}</RbLabel>
                        {(isOptional === true) ? <React.Fragment>
                            <RbLabel size={LabelSize.Small}>{(customizedOptionalText === "") ? "(Optional)" : customizedOptionalText}</RbLabel>
                        </React.Fragment> : null}
                    </div>
                    {/* Field */}
                    <div className={"ms-Grid-col ms-sm" + fieldWidth}>
                        {!!loadingElement ? loadingElement : field}
                    </div>
                </div>
            </div>
        );
    }

    static renderReadOnlyPeople(userIds: any, displayNames?: string, shouldBeText: boolean = false) {
        const containerClass = mergeStyles({
            display: 'flex',
            flexWrap: 'wrap',
        });

        let elements: any[] = []
        !!userIds && userIds.length > 0 && userIds.forEach((item: any) => {
            let userId = addAliasDomain(item);
            elements.push(
                <Person key={userId} userId={userId} disableImageFetch>
                    {shouldBeText
                        &&
                        <TemplatePersonOnlyText template='default' />
                        ||
                        <TemplatePerson template='default' />}
                    <TemplateLoadingPerson template='loading' />
                    <TemplateNotFoundPerson template='no-data' notFoundData={userId} shouldBeText={shouldBeText} />
                </Person>)
        })
        !!displayNames && displayNames !== "" && elements.push(<RbTag key={displayNames}>{displayNames}</RbTag>)
        return <div className={containerClass}>
            {elements}
        </div>
    }

    static renderReadOnlyBeautyPeople(userIds: any, displayNames?: string, shouldBeText: boolean = false) {
        const containerClass = mergeStyles({
            display: 'flex',
            flexWrap: 'wrap',
        });

        let elements: any[] = []
        !!userIds && userIds.length > 0 && userIds.forEach((item: any) => {
            let userId = addAliasDomain(item);
            elements.push(
                <Person key={userId} userId={userId} disableImageFetch>
                    {shouldBeText
                        &&
                        <BeautyTemplatePersonOnlyText template='default' />
                        ||
                        <BeautyTemplatePerson template='default' />}
                    <TemplateLoadingPerson template='loading' />
                    <BeautyTemplateNotFoundPerson template='no-data' notFoundData={userId} shouldBeText={shouldBeText} />
                </Person>)
        })
        !!displayNames && displayNames !== "" && elements.push(<RbTag key={displayNames}>{displayNames}</RbTag>)
        return <div className={containerClass}>
            {elements}
        </div>
    }

    static renderReadOnlyGroup(groupIds: any, displayNames?: string) {
        const containerClass = mergeStyles({
            display: 'flex',
            flexWrap: 'wrap',
        });

        let elements: any[] = []
        !!groupIds && groupIds.length > 0 && groupIds.forEach((groupId: any) => {
            elements.push(
                <Get resource={`/groups/${groupId}`} cacheEnabled={true}>
                    <TemplateGroupOnlyText template='default' />
                    <TemplateNotFoundPerson template='no-data' notFoundData={groupId} />
                    <TemplateLoadingPerson template='loading' />
                </Get>)
        })
        !!displayNames && displayNames !== "" && elements.push(<RbTag key={displayNames}>{displayNames}</RbTag>)
        return <div className={containerClass}>
            {elements}
        </div>
    }

    static renderReadOnlyBeautyGroup(groupIds: any, displayNames?: string) {
        const containerClass = mergeStyles({
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
        });

        let elements: any[] = []
        !!groupIds && groupIds.length > 0 && groupIds.forEach((groupId: any) => {
            elements.push(
                <Get key={groupId} resource={`/groups/${groupId}`} cacheEnabled={true}>
                    <BeautyTemplateGroup template='default' />
                    <BeautyTemplateNotFoundGroup template='no-data' notFoundData={groupId} />
                    <TemplateLoadingPerson template='loading' />
                </Get>)
        })
        !!displayNames && displayNames !== "" && elements.push(<RbTag key={displayNames}>{displayNames}</RbTag>)
        return <div className={containerClass}>
            {elements}
        </div>
    }

    static renderPeopleTextInlineTemplate(objectIds: string[], displayNames?: string) {
        let addAlias: string[] = []
        !!objectIds && objectIds.length > 0 && objectIds.forEach((objectId: string) => {
            addAlias.push(objectId.includes("@bosch.com") && objectId || `${objectId}@bosch.com`)
        })
        return (<>
            <People userIds={addAlias} showMax={10}>
                <TemplateNotFoundPeople template='no-data' />
                <TemplateDefaultPeople template='default' />
                <TemplateLoadingPeople template='loading' />
            </People>
            {!!displayNames &&
                <RbLabel
                    size={LabelSize.Small}
                    isInline={false}
                    isLongAuthor={true}
                >{displayNames}</RbLabel>
            }
        </>)
    }

    static renderReadOnlyPeoplePickerTags(text: string) {
        if (!_.isNil(text) && text !== "") {
            if (text.indexOf(";") > -1) {
                let authorsArray = text.split(";");
                return (
                    <Stack tokens={{ childrenGap: 5 }}>
                        {authorsArray.map((author: string, index: number) => <RbTag key={index}>{author}</RbTag>)}
                    </Stack>
                );
            }
            else {
                return (<RbTag>{text}</RbTag>);
            }
        }
        else {
            return "";
        }
    }


    static renderUploadTypeInfoRowTemplate(hoverstate: boolean, currenticon: string, entervar: any, closevar: any, iconid: string, field: any) { // show ,close info in Upload Choose
        return (
            <div className="ms-Grid upload-type-info">
                <div className="ms-Grid-row block-info">

                    <div className="select-info-icon">
                        <span className="rb-ic rb-ic-info-i-frame" onMouseEnter={entervar} id={iconid} />
                    </div>

                    {(hoverstate && (currenticon === iconid)) ?
                        <React.Fragment>
                            <div className="text-info">
                                <RbLabel size={LabelSize.Small}>{field}</RbLabel>
                                <div className="closed-info-icon" onClick={closevar}>
                                    <span className="rb-ic rb-ic-close" />
                                </div>
                            </div>

                        </React.Fragment>
                        : null}
                </div>
            </div>
        );

    }

    static renderUploadRowTemplate(label: string, field: any, info: string, smalltxt: string = "", infobox: boolean = true, errortxt: string = "", hasSmallTxt: boolean = true) {
        return (
            <div className="ms-Grid upload-row-template">
                <div className="ms-Grid-row mobile-gaps-row">

                    <div className="ms-Grid-col ms-sm2 upload-row-label-col" style={{ marginTop: "1rem" }}>
                        <RbLabel isInline={true} hasPadding={true}>{label}</RbLabel>
                        {hasSmallTxt ? <RbLabel isInline={true} size={LabelSize.Small}> &nbsp;{smalltxt}</RbLabel> : null}
                    </div>
                    <div className="ms-Grid-col ms-sm5 upload-row-field-col" style={{ padding: "1rem 0px" }}>
                        <div className="ms-Grid">
                            <div className={"ms-Grid-row " + (!!errortxt ? "error-field" : "")}>
                                {field}
                            </div>

                        </div>
                        {!!errortxt ? <div className="ms-Grid">
                            <div className="ms-Grid-row">
                                <RbLabel className="error-txt" size={LabelSize.Small} isInline={true} hasPadding={true}> {errortxt}</RbLabel>
                            </div>

                        </div> : null}



                    </div>
                    <div className="ms-Grid-col ms-sm5 upload-row-info-col">
                        {infobox ? <div className="upload-info-icon">
                            <span className="rb-ic rb-ic-info-i-frame" />
                        </div> : null}
                        <div className="upload-info">
                            <RbLabel size={LabelSize.Small}>{info}</RbLabel>
                        </div>
                    </div>

                </div>
            </div>
        );
    }

    static renderUploadSummaryTemplate(label: string, field: any, smalltxt: string = "") {
        return (
            <div className="ms-Grid">
                <div className="ms-Grid-row mobile-gaps-row">
                    <div className="ms-Grid-col ms-sm4" style={{ marginTop: "1rem" }}> <RbLabel isInline={true} style={{ fontWeight: "bold" }}>{label}</RbLabel> <RbLabel isInline={true} size={LabelSize.Small}>{smalltxt}</RbLabel> </div>
                    <div className="ms-Grid-col ms-sm8 upload-summary-field-col" style={{ padding: "1rem 0px" }}> {field} </div>
                </div>
            </div>
        );
    }

    static renderNote(note: string) {
        return (
            <div className="ms-Grid upload-note">
                <div className="ms-Grid-row">

                    <div className="ms-Grid-col ms-sm2">  </div>
                    <div className="ms-Grid-col ms-sm5"> <RbLabel isInline={true} size={LabelSize.Small} style={{ color: "#767676" }}> {note} </RbLabel>  </div>
                    <div className="ms-Grid-col ms-sm5">  </div>

                </div>
            </div>
        );
    }


    static renderCoordinatorNote(note: string) {
        return (
            <div className="ms-Grid upload-note upload-note-coordinator">
                <div className="ms-Grid-row">

                    <div className="ms-Grid-col ms-sm2">  </div>
                    <div className="ms-Grid-col ms-sm5" style={{ marginTop: "-30px" }}> <RbLabel isInline={true} size={LabelSize.Small} style={{ color: "#767676" }}> {note} </RbLabel>  </div>
                    <div className="ms-Grid-col ms-sm5">  </div>

                </div>
            </div>
        );
    }

    static renderNoteCustom(note: string) {
        return (
            <div className="ms-Grid upload-note upload-note-custom">
                <div className="ms-Grid-row">

                    <div className="ms-Grid-col ms-sm2">  </div>
                    <div className="ms-Grid-col ms-sm5" style={{ marginTop: "-15px" }}> <RbLabel isInline={true} size={LabelSize.Small} style={{ color: "#767676" }}> {note} </RbLabel>  </div>
                    <div className="ms-Grid-col ms-sm5">  </div>

                </div>
            </div>
        );
    }


}

export const TemplateDefaultPeople = (props: MgtTemplateProps) => {
    let peopleList = props.dataContext.people;
    let authorsAsString = "";
    !!peopleList && peopleList.forEach((person: IUser) => {
        if (person !== null && person !== undefined) {
            authorsAsString += `${person.displayName}; `
        }
    })
    return <div style={{ marginTop: "0.3rem" }}>
        <RbLabel
            size={LabelSize.Small}
            isInline={false}
            isLongAuthor={true}
        >{authorsAsString}</RbLabel>
    </div>
};

export const TemplateNotFoundPeople = (props: MgtTemplateProps) => {
    return <></>
};

export const TemplatePerson = (props: MgtTemplateProps) => {
    const person: IUser = props.dataContext.person;
    return <RbTag key={person.id}>{person.displayName}</RbTag>
};

export const BeautyTemplatePerson = (props: MgtTemplateProps) => {
    const person: IUser = props.dataContext.person;
    return <span
        key={person.id}
        style={{
            display: 'inline-block',
            backgroundColor: '#f8f9fa',
            color: '#323130',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            border: '1px solid #d2d0ce',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 'normal',
            fontFamily: 'BoschFont'
        }}
        title={`${person.displayName} (${person.userPrincipalName})`}
    >
        {person.displayName}
    </span>
};

export const BeautyPerson = (person: IUser) => {
    return <span
        key={person.id}
        style={{
            display: 'inline-block',
            backgroundColor: '#f8f9fa',
            color: '#323130',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            border: '1px solid #d2d0ce',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 'normal',
            fontFamily: 'BoschFont'
        }}
        title={`${person.displayName} (${person.userPrincipalName})`}
    >
        {person.displayName}
    </span>
}

export const BeautyTemplatePersonOnlyText = (props: MgtTemplateProps) => {
    const person: IUser = props.dataContext.person;
    return <span
        key={person.id}
        style={{
            display: 'inline-block',
            backgroundColor: '#f8f9fa',
            color: '#323130',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            border: '1px solid #d2d0ce',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 'normal',
            fontFamily: 'BoschFont'
        }}
        title={`${person.displayName} (${person.userPrincipalName})`}
    >
        {person.displayName}
    </span>
};

export const BeautyTemplateNotFoundPerson = (props: any) => {
    if (props.shouldBeText) {
        return <span
            style={{
                display: 'inline-block',
                backgroundColor: '#ffeaea',
                color: '#d32f2f',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                border: '1px solid #f5c6cb',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 'normal',
                fontFamily: 'BoschFont',
                fontStyle: 'italic'
            }}
            title={`User not found: ${props.notFoundData}`}
        >
            {props.notFoundData} (Not Found)
        </span>;
    }

    return <span
        style={{
            display: 'inline-block',
            backgroundColor: '#ffeaea',
            color: '#d32f2f',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            border: '1px solid #f5c6cb',
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 'normal',
            fontFamily: 'BoschFont'
        }}
        title={`User not found: ${props.notFoundData}`}
    >
        {props.notFoundData}
    </span>;
};

export const TemplateLoadingPeople = (props: any) => {
    return <Shimmer />
};

export const TemplatePersonOnlyText = (props: MgtTemplateProps) => {
    const person: IUser = props.dataContext.person;
    return <RbLabel hasPadding={true}>{person.displayName}</RbLabel>
};

export const TemplateNotFoundPerson = (props: any) => {
    return !!props.shouldBeText && props.notFoundData || <RbTag>{props.notFoundData}</RbTag>
};

export const TemplateGroupOnlyText = (props: MgtTemplateProps) => {
    const styles = mergeStyleSets({
        container: {
            border: '1px solid black',
            display: 'inline-block',
            fontSize: '13px',
            margin: '0.25rem',
        },
        personaRoot: {
            selectors: {
                '.ms-Persona-imageArea': {
                    display: 'none', // Hide the image
                },
                '.ms-Persona-primaryText': {
                    fontSize: '13px',
                },
                '.ms-Persona-secondaryText': {
                    fontSize: '12px',
                },
            },
        },
    });
    const group: IGroup = props.dataContext;
    const persona: IPersonaProps = {
        text: group.displayName ?? "",
        secondaryText: group.mail ?? "",
        tertiaryText: group.description ?? "",
        showSecondaryText: true
    }
    return <div className={styles.container}>
        <Persona {...persona} className={styles.personaRoot} />
    </div>
}

export const BeautyTemplateGroup = (props: MgtTemplateProps) => {
    const group: IGroup = props.dataContext;
    return <span
        key={group.id}
        style={{
            display: 'inline-block',
            backgroundColor: '#e3f2fd',
            color: '#0d47a1',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            border: '1px solid #90caf9',
            maxWidth: '250px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 'normal',
            fontFamily: 'BoschFont'
        }}
        title={`${group.displayName}${group.mail ? ` (${group.mail})` : ''}`}
    >
        {group.displayName}
    </span>
};

export const BeautyTemplateNotFoundGroup = (props: any) => {
    return <span
        style={{
            display: 'inline-block',
            backgroundColor: '#ffeaea',
            color: '#d32f2f',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            border: '1px solid #f5c6cb',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 'normal',
            fontFamily: 'BoschFont',
            fontStyle: 'italic'
        }}
        title={`Group not found: ${props.notFoundData}`}
    >
        {props.notFoundData} (Not Found)
    </span>;
};

export const TemplateLoadingPerson = (props: any) => {
    return <Shimmer />
};

export const PersonCardNotFoundTemplate = (props: MgtTemplateProps) => {
    return (
        <div style={{ padding: '1rem', backgroundColor: '#fde7e9', borderRadius: '5px', fontFamily: 'BoschFont' }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#d83b01' }}>Person Not Found</h3>
            <p style={{ color: '#a4262c' }}>
                We couldn't find the person details. Please check again later.
            </p>
        </div>
    );
}

export const PersonCardTemplate = (props: MgtTemplateProps) => {
    const personDetails: IPersonDetail = props.dataContext.person;
    return (
        <>
            <div style={{ padding: '1rem', backgroundColor: '#f3f2f1', borderRadius: '5px' }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#0078d4' }}>
                    About {personDetails.displayName}
                </h3>
                <p>
                    <strong>Department:</strong> {personDetails.department}
                </p>
                <p>
                    <strong>User Principal Name:</strong> {personDetails.userPrincipalName}
                </p>
                <p>
                    <strong>Mail:</strong> {personDetails.mail || ""}
                </p>
                <button
                    style={{
                        marginTop: '10px',
                        padding: '0.5rem 1rem',
                        backgroundColor: '#0078d4',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                    }}
                    onClick={() => {
                        if (personDetails?.userPrincipalName) {
                            const teamsLink = `https://teams.microsoft.com/l/chat/0/0?users=${personDetails.userPrincipalName}`;
                            window.open(teamsLink, "_blank"); // Open the Teams link in a new tab
                        } else {
                            alert("User email not available for chat.");
                        }
                    }}
                >
                    Chat via Teams
                </button>
            </div>
            {!personDetails.userRole || personDetails.userRole.length === 0 ?
                <div style={{ padding: '1rem', backgroundColor: '#f3f2f1', borderRadius: '5px', textAlign: 'center', fontFamily: 'BoschFont' }}>
                    <h3 style={{ margin: '0 0 10px 0', color: '#0078d4' }}>
                        Their roles:
                    </h3>
                    <p>No roles found for this person.</p>
                </div>
                : <div style={{ padding: '1rem', backgroundColor: '#f3f2f1', borderRadius: '5px', fontFamily: 'BoschFont' }}>
                    <h3 style={{ margin: '0 0 10px 0', color: '#0078d4' }}>
                        Their roles:
                    </h3>
                    <ul style={{ paddingLeft: '1rem', listStyleType: 'circle', color: '#333' }}>
                        {personDetails.userRole.map((role: string, index: number) => (
                            <li key={index} style={{ marginBottom: '5px' }}>
                                {role}
                            </li>
                        ))}
                    </ul>
                </div>}
        </>
    )
}
export const SupportEmailTemplate = `Service Group: FEBER%0D%0A%0D%0A
Please help us answer these questions for better/faster support:%0D%0A%0D%0A
Q: Please describe your issue in details.%0D%0A
A:%0D%0A%0D%0A
Q: If possible, please provide the screenshots of the issue.%0D%0A
A:%0D%0A%0D%0A
Q: Please provide us the link/url, which causes the issue.%0D%0A
A:%0D%0A%0D%0A
Q: Please provide the NTID of the user, who has the issue.%0D%0A
A:%0D%0A%0D%0A
Note: If you are going to update the attachment, please remember to attach it into this email.%0D%0A`;

export const renderSpinnerWithLabel = (label: string = "Loading...", position: SpinnerLabelPosition = "bottom") => <Spinner label={label} ariaLive="assertive" labelPosition={position} />

export default Template;